import { createFileRoute, notFound } from "@tanstack/react-router";
import { ForexGuideView } from "@/components/forex-guide-view";
import { getForexGuide } from "@/lib/forex-guides";

export const Route = createFileRoute("/guides/$slug")({
  loader: ({ params }) => {
    const guide = getForexGuide(params.slug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData?.guide) {
      return { meta: [{ title: "Guide not found — ForexMind AI" }] };
    }
    const g = loaderData.guide;
    const url = `https://forexmind.ai/guides/${params.slug}`;

    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://forexmind.ai/" },
        { "@type": "ListItem", position: 2, name: "Guides", item: "https://forexmind.ai/guides" },
        { "@type": "ListItem", position: 3, name: g.heading ?? g.title, item: url },
      ],
    };

    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: g.heading ?? g.title,
      description: g.description,
      mainEntityOfPage: url,
      datePublished: g.updatedAt,
      dateModified: g.updatedAt,
      author: { "@type": "Organization", name: "ForexMind AI", url: "https://forexmind.ai" },
      publisher: {
        "@type": "Organization",
        name: "ForexMind AI",
        logo: { "@type": "ImageObject", url: "https://forexmind.ai/favicon.ico" },
      },
      inLanguage: "en",
    };

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: g.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    const jsonLd: object[] = [breadcrumbLd, articleLd, faqLd];

    if (g.steps && g.steps.length > 0) {
      jsonLd.push({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: g.heading ?? g.title,
        description: g.aiAnswer,
        step: g.steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.description,
        })),
      });
    }

    return {
      meta: [
        { title: g.title },
        { name: "description", content: g.description },
        { property: "og:title", content: g.title },
        { property: "og:description", content: g.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://forexmind.ai/og-image.png" },
        { property: "article:published_time", content: g.updatedAt },
        { property: "article:modified_time", content: g.updatedAt },
        { property: "article:section", content: g.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: g.title },
        { name: "twitter:description", content: g.description },
        { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: jsonLd.map((ld) => ({
        type: "application/ld+json",
        children: JSON.stringify(ld),
      })),
    };
  },
  component: GuidePage,
  notFoundComponent: () => (
    <div className="container mx-auto max-w-2xl px-6 py-20 text-center">
      <h1 className="mb-4 text-3xl font-bold">Guide not found</h1>
      <p className="mb-6 text-muted-foreground">
        That guide does not exist. Browse all guides below.
      </p>
      <a href="/guides" className="text-primary underline">
        All guides
      </a>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="container mx-auto max-w-2xl px-6 py-20 text-center">
      <h1 className="mb-4 text-3xl font-bold">Something went wrong</h1>
      <p className="mb-6 text-muted-foreground">{error.message}</p>
      <button onClick={reset} className="text-primary underline">
        Try again
      </button>
    </div>
  ),
});

function GuidePage() {
  const { guide } = Route.useLoaderData();
  return <ForexGuideView guide={guide} />;
}
